import { useState } from 'react';

import { MarketingBannersStack } from '@cast/design-system';

import { useIsClusterDemoMode } from '@cast/infrastructure-demo';
import { useIsOpsPilotEnabled } from '@cast/features-organization-shared-opspilot';
import { useAbility } from '@cast/shared-ability-data-access';
import { useShouldLimitConsoleForReporting } from '@cast/shared-trial-ui';
import { useIsOrganizationAuditLogVisible } from 'hooks/permissions/useIsOrganizationAuditLogVisible';

import {
  addClosedAlert,
  isAlertDismissed,
  makePersonalizedAlertId,
} from 'components/alerts/CloseableAlert/utils';
import { CAST_MCP_BANNER_ID, CastMCPBanner } from '../banners/CastMCPBanner';
import {
  CAST_AI_CHATBOT_BANNER_ID,
  CastAIChatbotBanner,
} from '../banners/CastAIChatbotBanner';
import {
  SLACK_ANNOUNCEMENT_BANNER_ID,
  SlackAnnouncementBanner,
} from '../banners/SlackAnnouncementBanner';
import {
  AIE_SERVERLESS_MODELS_BANNER_ID,
  AIEServerlessModelsBanner,
} from '../banners/AIEServerlessModelsBanner';
import {
  EVENT_LOG_PROMOTION_BANNER_ID,
  EventLogPromotionalBanner,
} from '../banners/EventLogPromotionalBanner';

export const DashboardBanners = () => {
  const isDemoMode = useIsClusterDemoMode();
  const { opsPilotStatus, tryOpsPilotDisabledTooltip } = useIsOpsPilotEnabled();
  const ability = useAbility();
  const limitReportingConsole = useShouldLimitConsoleForReporting();
  const canViewServerlessModels = ability.can(
    'view',
    'FeatureAIEServerlessModelsNotification'
  );
  const isOrganizationAuditLogVisible = useIsOrganizationAuditLogVisible();

  const [enabledMap, setEnabledMap] = useState<Record<string, boolean>>({
    [CAST_MCP_BANNER_ID]: !isAlertDismissed(CAST_MCP_BANNER_ID),
    [CAST_AI_CHATBOT_BANNER_ID]:
      !isAlertDismissed(CAST_AI_CHATBOT_BANNER_ID) &&
      opsPilotStatus === 'on' &&
      !tryOpsPilotDisabledTooltip,
    [SLACK_ANNOUNCEMENT_BANNER_ID]: !isAlertDismissed(
      SLACK_ANNOUNCEMENT_BANNER_ID
    ),
    [AIE_SERVERLESS_MODELS_BANNER_ID]:
      !isAlertDismissed(AIE_SERVERLESS_MODELS_BANNER_ID) &&
      canViewServerlessModels,
    [EVENT_LOG_PROMOTION_BANNER_ID]:
      !isAlertDismissed(EVENT_LOG_PROMOTION_BANNER_ID) &&
      isOrganizationAuditLogVisible,
  });

  if (isDemoMode) {
    return null;
  }

  // MCP is Full reporting only — hide promo when the console is reporting-limited.
  const showMcpBanner =
    !limitReportingConsole && enabledMap[CAST_MCP_BANNER_ID];

  return (
    <MarketingBannersStack
      testId="dashboard-notifications"
      sx={{ isolation: 'isolate' }}
    >
      {showMcpBanner && (
        <CastMCPBanner
          key={CAST_MCP_BANNER_ID}
          onClose={() => {
            addClosedAlert(makePersonalizedAlertId(CAST_MCP_BANNER_ID));
            setEnabledMap({ ...enabledMap, [CAST_MCP_BANNER_ID]: false });
          }}
        />
      )}
      {enabledMap[CAST_AI_CHATBOT_BANNER_ID] && (
        <CastAIChatbotBanner
          key={CAST_AI_CHATBOT_BANNER_ID}
          onClose={() => {
            addClosedAlert(makePersonalizedAlertId(CAST_AI_CHATBOT_BANNER_ID));
            setEnabledMap({
              ...enabledMap,
              [CAST_AI_CHATBOT_BANNER_ID]: false,
            });
          }}
        />
      )}
      {enabledMap[SLACK_ANNOUNCEMENT_BANNER_ID] && (
        <SlackAnnouncementBanner
          key={SLACK_ANNOUNCEMENT_BANNER_ID}
          onClose={() => {
            addClosedAlert(
              makePersonalizedAlertId(SLACK_ANNOUNCEMENT_BANNER_ID)
            );
            setEnabledMap({
              ...enabledMap,
              [SLACK_ANNOUNCEMENT_BANNER_ID]: false,
            });
          }}
        />
      )}
      {enabledMap[AIE_SERVERLESS_MODELS_BANNER_ID] && (
        <AIEServerlessModelsBanner
          key={AIE_SERVERLESS_MODELS_BANNER_ID}
          onClose={() => {
            addClosedAlert(
              makePersonalizedAlertId(AIE_SERVERLESS_MODELS_BANNER_ID)
            );
            setEnabledMap({
              ...enabledMap,
              [AIE_SERVERLESS_MODELS_BANNER_ID]: false,
            });
          }}
        />
      )}
      {enabledMap[EVENT_LOG_PROMOTION_BANNER_ID] && (
        <EventLogPromotionalBanner
          key={EVENT_LOG_PROMOTION_BANNER_ID}
          onClose={() => {
            addClosedAlert(
              makePersonalizedAlertId(EVENT_LOG_PROMOTION_BANNER_ID)
            );
            setEnabledMap({
              ...enabledMap,
              [EVENT_LOG_PROMOTION_BANNER_ID]: false,
            });
          }}
        />
      )}
    </MarketingBannersStack>
  );
};
